import { Search } from 'lucide-react'
import { useState } from 'react'
import { Input } from '@/components/ui/input'
import { useDraggable } from '@dnd-kit/core'
import { cn } from '@/lib/utils/cn'

interface FieldTypeItem {
  type: string
  label: string
  icon: string
  description: string
}

const FIELD_TYPES: FieldTypeItem[] = [
  { type: 'text', label: 'متن', icon: '🔤', description: 'متن کوتاه' },
  { type: 'textarea', label: 'متن بلند', icon: '📝', description: 'پاراگراف' },
  { type: 'number', label: 'عدد', icon: '🔢', description: 'عدد صحیح یا اعشاری' },
  { type: 'date', label: 'تاریخ', icon: '📅', description: 'شمسی یا میلادی' },
  { type: 'time', label: 'زمان', icon: '⏰', description: 'ساعت و دقیقه' },
  { type: 'datetime', label: 'تاریخ و زمان', icon: '🕐', description: 'ترکیبی' },
  { type: 'single_select', label: 'انتخاب تکی', icon: '⊙', description: 'یک گزینه' },
  { type: 'multi_select', label: 'انتخاب چندگانه', icon: '☑', description: 'چند گزینه' },
  { type: 'boolean', label: 'بله/خیر', icon: '✓', description: 'سوئیچ' },
  { type: 'file', label: 'فایل', icon: '📎', description: 'بارگذاری فایل' },
  { type: 'image', label: 'تصویر', icon: '🖼', description: 'بارگذاری تصویر' },
  { type: 'calculated', label: 'محاسباتی', icon: '🧮', description: 'فرمول خودکار' },
  { type: 'repeatable_array', label: 'آرایه تکرارپذیر', icon: '🔁', description: 'چند ردیف' },
]

function DraggablePaletteItem({
  ft,
  onAddField,
}: {
  ft: FieldTypeItem
  onAddField: (type: string) => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${ft.type}`,
    data: { fieldType: ft.type },
  })

  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onAddField(ft.type)}
      className={cn(
        'w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-start hover:bg-muted transition-colors group cursor-grab active:cursor-grabbing select-none',
        isDragging && 'opacity-40'
      )}
    >
      <span className="text-base shrink-0">{ft.icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground group-hover:text-primary transition-colors">
          {ft.label}
        </p>
        <p className="text-[10px] text-muted-foreground">{ft.description}</p>
      </div>
    </button>
  )
}

export { FIELD_TYPES }

interface FieldPaletteProps {
  onAddField: (type: string) => void
}

export function FieldPalette({ onAddField }: FieldPaletteProps) {
  const [search, setSearch] = useState('')

  const filtered = FIELD_TYPES.filter(
    (ft) =>
      ft.label.includes(search) ||
      ft.type.includes(search.toLowerCase()) ||
      ft.description.includes(search)
  )

  return (
    <div className="p-3 space-y-2">
      <p className="text-xs font-medium text-muted-foreground px-1 mb-3">انواع فیلد</p>
      <div className="relative mb-2">
        <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="جستجو..."
          className="h-7 ps-8 text-xs"
        />
      </div>
      <div className="space-y-1">
        {filtered.map((ft) => (
          <DraggablePaletteItem key={ft.type} ft={ft} onAddField={onAddField} />
        ))}
      </div>
    </div>
  )
}
